import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import { mrtCaseSchema } from "@/components/Forms/schema/mrtCaseSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { localDateTransformer } from "@/utils/zodTransformers.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";
import { z } from "zod";
import type { CaseFilter } from "./useFetchCaseFiltersets";

export const personSchema = z.object({
    idUUID: z.string(),
    id: z.number(),
    dateofbirth: localDateTransformer,
});

export const caseResourceSelector = createResourceCollectionSelector({
    type: "case",
    attributesSchema: mrtCaseSchema,
    documentMetaSchema: dtMetaSchema,
    relationships: {
        idPeople: {
            relationshipType: "one_nullable",
            include: {
                type: "person",
                attributesSchema: personSchema,
            },
            required: false,
        },
    },
});

export const casesSelector = createDataSelector(caseResourceSelector);
export type DashboardCases = ReturnType<typeof casesSelector>;

export type DashboardCasesDocument = ReturnType<typeof caseResourceSelector>;
export type MrtDashboardCase = DashboardCasesDocument["data"][number];

type Props = {
    isQueryEnabled?: boolean;
    selectedTab?: string;
    taskDashboard?: boolean;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    activeFilter: CaseFilter | null;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
    searchFields: string[];
};

export const useFetchMrtDashboardCases = ({
    isQueryEnabled = true,
    selectedTab = "",
    taskDashboard = true,
    columnFilters,
    globalFilter,
    activeFilter,
    pagination,
    sorting,
    searchFields,
}: Props): UseQueryResult<DashboardCasesDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "dashboard-cases-table-data",
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
            searchFields,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: columnFilters ?? [],
                globalFilter: globalFilter ?? "",
                activeFilter: activeFilter,
                sorting: sorting ?? [],
                selectedTab: selectedTab,
                taskDashboard: taskDashboard,
                searchFields: searchFields,
            };

            const response = await fetch(apiUrl("/dashboard/cases"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: caseResourceSelector,
        staleTime: 1000 * 30, //30 seconds
    });
};
